<template>
  <div class="daily-chart">
    <div class="daily-chart__header">
      <h3 class="daily-chart__title">日別推移</h3>
      <div class="daily-chart__legend">
        <span class="daily-chart__legend-item">
          <span class="daily-chart__legend-swatch" :style="{ background: colors.imp }"></span>
          表示数
        </span>
        <span class="daily-chart__legend-item">
          <span class="daily-chart__legend-swatch" :style="{ background: colors.click }"></span>
          クリック数
        </span>
        <span class="daily-chart__legend-item">
          <span class="daily-chart__legend-line" :style="{ background: colors.ctr }"></span>
          CTR
        </span>
      </div>
    </div>

    <div class="daily-chart__svg-wrap">
      <svg :viewBox="`0 0 ${W} ${H}`" preserveAspectRatio="xMidYMid meet" class="daily-chart__svg">
        <!-- Y軸グリッド -->
        <line
          v-for="(t, i) in leftTicks"
          :key="'grid-' + i"
          :x1="left"
          :x2="W - right"
          :y1="t.y"
          :y2="t.y"
          stroke="#e5e5e5"
          stroke-dasharray="3 3"
        />

        <!-- 左Y軸ラベル (表示数/クリック数) -->
        <text
          v-for="(t, i) in leftTicks"
          :key="'ly-' + i"
          :x="left - 6"
          :y="t.y + 3"
          text-anchor="end"
          class="daily-chart__axis"
        >{{ formatCount(t.value) }}</text>

        <!-- 右Y軸ラベル (CTR%) -->
        <text
          v-for="(t, i) in rightTicks"
          :key="'ry-' + i"
          :x="W - right + 6"
          :y="t.y + 3"
          text-anchor="start"
          class="daily-chart__axis"
        >{{ formatPct(t.value) }}%</text>

        <!-- 軸線 -->
        <line
          :x1="left"
          :x2="W - right"
          :y1="top + innerH"
          :y2="top + innerH"
          stroke="#cccccc"
        />
        <line
          :x1="left"
          :x2="left"
          :y1="top"
          :y2="top + innerH"
          stroke="#cccccc"
        />
        <line
          :x1="W - right"
          :x2="W - right"
          :y1="top"
          :y2="top + innerH"
          stroke="#cccccc"
        />

        <!-- X軸ラベル -->
        <template v-for="(d, i) in processedData" :key="'xl-' + i">
          <text
            v-if="shouldShowXLabel(i)"
            :x="bandCenterX(i)"
            :y="top + innerH + 16"
            text-anchor="middle"
            class="daily-chart__axis"
          >{{ d.label }}</text>
        </template>

        <!-- バー: 表示数 + クリック数 -->
        <g v-for="(d, i) in processedData" :key="'bar-' + i">
          <rect
            :x="bandX(i) + bandW * 0.18"
            :y="top + innerH - d.impH"
            :width="bandW * 0.30"
            :height="d.impH"
            :fill="colors.imp"
            rx="2"
            ry="2"
          />
          <rect
            :x="bandX(i) + bandW * 0.52"
            :y="top + innerH - d.clickH"
            :width="bandW * 0.30"
            :height="d.clickH"
            :fill="colors.click"
            rx="2"
            ry="2"
          />
        </g>

        <!-- CTR 折れ線 (null で分断) -->
        <polyline
          v-for="(seg, i) in ctrSegments"
          :key="'seg-' + i"
          :points="seg"
          fill="none"
          :stroke="colors.ctr"
          stroke-width="2"
        />
        <circle
          v-for="(p, i) in ctrPoints"
          :key="'pt-' + i"
          :cx="p.x"
          :cy="p.y"
          r="3"
          :fill="colors.ctr"
        />

        <!-- ホバー領域 -->
        <rect
          v-for="(d, i) in processedData"
          :key="'hov-' + i"
          :x="bandX(i)"
          :y="top"
          :width="bandW"
          :height="innerH"
          fill="#000000"
          fill-opacity="0"
          class="daily-chart__hover-zone"
          @mousemove="onHover($event, d)"
          @mouseleave="onLeave"
        />
      </svg>

      <!-- ツールチップ -->
      <div
        v-if="tooltip.visible"
        class="daily-chart__tooltip"
        :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }"
      >
        <div class="daily-chart__tooltip-title">{{ tooltip.fullDate }}</div>
        <div class="daily-chart__tooltip-row">
          <span class="daily-chart__tooltip-label">
            <span class="daily-chart__legend-swatch" :style="{ background: colors.imp }"></span>
            <span :style="{ color: colors.imp }">表示数</span>
          </span>
          <span class="daily-chart__tooltip-value">{{ formatCount(tooltip.imp) }}</span>
        </div>
        <div class="daily-chart__tooltip-row">
          <span class="daily-chart__tooltip-label">
            <span class="daily-chart__legend-swatch" :style="{ background: colors.click }"></span>
            <span :style="{ color: colors.click }">クリック数</span>
          </span>
          <span class="daily-chart__tooltip-value">{{ formatCount(tooltip.click) }}</span>
        </div>
        <div v-if="tooltip.ctr !== null" class="daily-chart__tooltip-row">
          <span class="daily-chart__tooltip-label">
            <span class="daily-chart__legend-swatch" :style="{ background: colors.ctr }"></span>
            <span :style="{ color: colors.ctr }">CTR</span>
          </span>
          <span class="daily-chart__tooltip-value">{{ tooltip.ctr.toFixed(2) }}%</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    // 日別集計データ配列。 summary_date (YYYY-MM-DD), display_count, click_count を持つ行の配列
    data: { type: Array, default: function () { return []; } },
  },
  data: function () {
    return {
      // viewBox 基準のサイズ。実寸は親幅に追従する (width: 100%, height: auto)
      W: 1100,
      H: 320,
      top: 14,
      right: 52,
      left: 56,
      bottom: 38,
      // デバイス別集計のモバイル/PC/タブレット 3色に統一 (トンマナ調整指示)
      colors: {
        imp: '#3cb6d2',   // モバイル
        click: '#209bb9', // PC
        ctr: '#8ac7d5',   // タブレット
      },
      tooltip: { visible: false, x: 0, y: 0, fullDate: '', imp: 0, click: 0, ctr: null },
    };
  },
  computed: {
    innerW: function () { return this.W - this.left - this.right; },
    innerH: function () { return this.H - this.top - this.bottom; },
    bandW: function () {
      var n = Math.max(this.processedData.length, 1);
      return this.innerW / n;
    },
    processedData: function () {
      var rows = (this.data || []).slice().sort(function (a, b) {
        return String(a.summary_date).localeCompare(String(b.summary_date));
      });
      var self = this;
      var leftMax = self.leftMax;
      return rows.map(function (r) {
        var display = Number(r.display_count || 0);
        var click = Number(r.click_count || 0);
        // CTR はどちらかがゼロなら null (折れ線を分断して表現)
        var ctr = display > 0 && click > 0 ? (click * 100 / display) : null;
        return {
          rawDate: r.summary_date,
          label: self.shortLabel(r.summary_date),
          fullDate: self.fullDate(r.summary_date),
          imp: display,
          click: click,
          ctr: ctr,
          impH: leftMax > 0 ? (display / leftMax) * self.innerH : 0,
          clickH: leftMax > 0 ? (click / leftMax) * self.innerH : 0,
        };
      });
    },
    leftMax: function () {
      var max = 0;
      (this.data || []).forEach(function (r) {
        var d = Number(r.display_count || 0);
        var c = Number(r.click_count || 0);
        if (d > max) max = d;
        if (c > max) max = c;
      });
      return this.niceCeil(max);
    },
    rightMax: function () {
      var max = 0;
      (this.data || []).forEach(function (r) {
        var d = Number(r.display_count || 0);
        var c = Number(r.click_count || 0);
        if (d > 0 && c > 0) {
          var ctr = c * 100 / d;
          if (ctr > max) max = ctr;
        }
      });
      // 最大100%、下限10%。分かりやすい刻みに丸める
      return Math.min(100, Math.max(10, this.niceCeil(max)));
    },
    leftTicks: function () {
      var ticks = [];
      var n = 5;
      for (var i = 0; i <= n; i++) {
        ticks.push({
          value: this.leftMax * i / n,
          y: this.top + this.innerH - (i / n) * this.innerH,
        });
      }
      return ticks;
    },
    rightTicks: function () {
      var ticks = [];
      var n = 5;
      for (var i = 0; i <= n; i++) {
        ticks.push({
          value: this.rightMax * i / n,
          y: this.top + this.innerH - (i / n) * this.innerH,
        });
      }
      return ticks;
    },
    ctrPoints: function () {
      var self = this;
      var out = [];
      this.processedData.forEach(function (d, i) {
        if (d.ctr === null) return;
        out.push({
          x: self.bandCenterX(i),
          y: self.top + self.innerH - (d.ctr / self.rightMax) * self.innerH,
        });
      });
      return out;
    },
    ctrSegments: function () {
      var self = this;
      var segs = [];
      var cur = [];
      this.processedData.forEach(function (d, i) {
        if (d.ctr === null) {
          if (cur.length >= 2) segs.push(cur.join(' '));
          cur = [];
          return;
        }
        var x = self.bandCenterX(i);
        var y = self.top + self.innerH - (d.ctr / self.rightMax) * self.innerH;
        cur.push(x + ',' + y);
      });
      if (cur.length >= 2) segs.push(cur.join(' '));
      return segs;
    },
    xLabelStep: function () {
      // ラベル (例: "04/12") が概ね 32px 幅必要なので、バンド幅に応じて間引く
      var b = this.bandW;
      if (b >= 36) return 1;
      if (b >= 24) return 2;
      if (b >= 16) return 3;
      return Math.ceil(32 / Math.max(b, 1));
    },
  },
  methods: {
    bandX: function (i) { return this.left + this.bandW * i; },
    bandCenterX: function (i) { return this.bandX(i) + this.bandW / 2; },
    shouldShowXLabel: function (i) {
      return i % this.xLabelStep === 0;
    },
    shortLabel: function (dateStr) {
      var parts = String(dateStr || '').split('-');
      if (parts.length >= 3) return parts[1] + '/' + parts[2];
      return String(dateStr || '');
    },
    fullDate: function (dateStr) {
      return String(dateStr || '').replace(/-/g, '/');
    },
    niceCeil: function (v) {
      if (!v || v <= 0) return 1;
      var mag = Math.pow(10, Math.floor(Math.log10(v)));
      var norm = v / mag;
      var nice;
      if (norm <= 1) nice = 1;
      else if (norm <= 2) nice = 2;
      else if (norm <= 5) nice = 5;
      else nice = 10;
      return nice * mag;
    },
    formatCount: function (v) {
      if (v === null || v === undefined) return '';
      var n = Number(v);
      // Y軸ラベルが小数になる可能性に備えて整数丸め
      return Math.round(n).toLocaleString('ja-JP');
    },
    formatPct: function (v) {
      var n = Number(v);
      return n >= 10 ? n.toFixed(0) : n.toFixed(1);
    },
    onHover: function (evt, d) {
      var rect = this.$el.getBoundingClientRect();
      this.tooltip = {
        visible: true,
        x: evt.clientX - rect.left + 12,
        y: evt.clientY - rect.top + 12,
        fullDate: d.fullDate,
        imp: d.imp,
        click: d.click,
        ctr: d.ctr,
      };
    },
    onLeave: function () {
      this.tooltip.visible = false;
    },
  },
};
</script>

<style scoped>
.daily-chart {
    position: relative;
    background: #ffffff;
    border: 1px solid #cccccc;
    border-radius: 4px;
    padding: 18px 20px 16px;
    margin-top: 16px;
    font-family: "Hiragino Kaku Gothic ProN", "Noto Sans JP", Meiryo, sans-serif;
    color: #333333;
}

.daily-chart__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    border-bottom: 2px solid #3cb6d2;
    padding-bottom: 10px;
    margin-bottom: 16px;
}

.daily-chart__title {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: #209bb9;
    letter-spacing: 0.02em;
}

.daily-chart__legend {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
}

.daily-chart__legend-item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #666666;
}

.daily-chart__legend-swatch {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 2px;
    flex-shrink: 0;
}

.daily-chart__legend-line {
    display: inline-block;
    width: 18px;
    height: 2px;
    border-radius: 1px;
    flex-shrink: 0;
}

.daily-chart__svg-wrap {
    position: relative;
    width: 100%;
}

.daily-chart__svg {
    width: 100%;
    height: auto;
    display: block;
}

.daily-chart__axis {
    font-size: 11px;
    fill: #666666;
}

.daily-chart__hover-zone {
    cursor: crosshair;
    transition: fill-opacity 0.1s;
}

.daily-chart__hover-zone:hover {
    fill-opacity: 0.04;
}

.daily-chart__tooltip {
    position: absolute;
    background: #ffffff;
    border: 1px solid #cccccc;
    border-radius: 4px;
    padding: 8px 12px;
    font-size: 12px;
    color: #333333;
    min-width: 180px;
    pointer-events: none;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
    z-index: 10;
}

.daily-chart__tooltip-title {
    font-weight: 700;
    margin-bottom: 6px;
    padding-bottom: 4px;
    border-bottom: 1px solid #efefef;
}

.daily-chart__tooltip-row {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    margin-top: 2px;
}

.daily-chart__tooltip-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.daily-chart__tooltip-value {
    font-weight: 700;
    color: #333333;
}
</style>
